import axios from 'axios';

class FrontendErrorLogger {
  constructor(apiEndpoint = '/api/logs/frontend') {
    this.apiEndpoint = apiEndpoint;
    this.sessionId = this.generateSessionId();
    this.setupGlobalErrorHandlers();
  }

  generateSessionId() {
    return 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  setupGlobalErrorHandlers() {
    // Global JavaScript errors
    window.addEventListener('error', (event) => {
      this.logError({
        type: 'javascript_error',
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
        url: window.location.href,
        userAgent: navigator.userAgent
      });
    });

    // Unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.logError({
        type: 'unhandled_promise_rejection',
        message: event.reason?.message || 'Unhandled Promise Rejection',
        stack: event.reason?.stack,
        url: window.location.href,
        userAgent: navigator.userAgent
      });
    });

    // Vue.js specific errors (if using Vue 3)
    if (window.Vue) {
      const app = window.Vue.getCurrentInstance()?.appContext?.app;
      if (app) {
        app.config.errorHandler = (err, vm, info) => {
          this.logError({
            type: 'vue_error',
            message: err.message,
            stack: err.stack,
            componentInfo: info,
            url: window.location.href,
            userAgent: navigator.userAgent
          });
        };
      }
    }
  }

  async logError(errorData) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      level: 'error',
      ...errorData,
      // Additional context
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      browserInfo: {
        language: navigator.language,
        platform: navigator.platform,
        cookieEnabled: navigator.cookieEnabled
      }
    };

    try {
      await axios.post(this.apiEndpoint, logEntry, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
    } catch (error) {
      // Fallback to console if API fails
      console.error('Failed to log error to server:', error);
      console.error('Original error:', logEntry);
    }
  }

  // Manual error logging
  logCustomError(message, additionalData = {}) {
    this.logError({
      type: 'custom_error',
      message,
      ...additionalData
    });
  }

  // API error logging (updated for axios response object)
  logApiError(error, requestData = {}) {
    const errorInfo = {
      type: 'api_error',
      requestData: JSON.stringify(requestData)
    };

    if (error.response) {
      // Server responded with error status
      errorInfo.message = `API Error: ${error.response.status} ${error.response.statusText}`;
      errorInfo.status = error.response.status;
      errorInfo.statusText = error.response.statusText;
      errorInfo.url = error.response.config?.url;
      errorInfo.responseData = JSON.stringify(error.response.data);
    } else if (error.request) {
      // Request was made but no response received
      errorInfo.message = 'API Error: No response received';
      errorInfo.url = error.config?.url;
    } else {
      // Something else happened
      errorInfo.message = `API Error: ${error.message}`;
    }

    this.logError(errorInfo);
  }
}

// Initialize and export
const errorLogger = new FrontendErrorLogger();
export default errorLogger;

// Vue plugin version (optional)
export const ErrorLoggerPlugin = {
  install(app, options = {}) {
    const logger = new FrontendErrorLogger(options.apiEndpoint);
    
    app.config.globalProperties.$errorLogger = logger;
    app.provide('errorLogger', logger);
    
    // Global error handler
    app.config.errorHandler = (err, vm, info) => {
      logger.logError({
        type: 'vue_error',
        message: err.message,
        stack: err.stack,
        componentInfo: info,
        url: window.location.href
      });
    };
  }
};